import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';

import AuthMethodSelector from './components/AuthMethodSelector';
import StandardLoginForm from './components/StandardLoginForm';
import OTPForm from './components/OTPForm';
import FaceIDForm from './components/FaceIDForm';
import SecurityBadges from './components/SecurityBadges';
import LoginUtilities from './components/LoginUtilities';

const LoginAuthentication = () => {
  const navigate = useNavigate();
  const { signIn, user, loading: authLoading } = useAuth();
  const [authMethod, setAuthMethod] = useState('standard');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/employee-dashboard');
    }
  }, [user, authLoading, navigate]);

  const handleAuthSubmit = async (formData) => {
    setLoading(true);
    setError('');

    try {
      if (authMethod === 'standard') {
        if (!formData?.email || !formData?.password) {
          setError('Please enter both email and password');
          return;
        }

        const { data, error: authError } = await signIn(formData?.email, formData?.password);
        
        if (authError) {
          setError(authError?.message || 'Invalid email or password');
          return;
        }

        if (data?.user) {
          // Store remember me preference
          if (rememberMe) {
            localStorage.setItem('rememberMe', 'true');
          } else {
            localStorage.removeItem('rememberMe');
          }
          
          // Navigation will be handled by useEffect when user state changes
        }
      } else if (authMethod === 'otp') {
        // OTP method would be implemented with SMS/Email OTP
        setError('OTP authentication not implemented yet. Please use email/password.');
      } else if (authMethod === 'faceid') {
        // Face ID authentication is handled by FaceIDForm component
        // The form will call this function with authenticated data
        if (formData?.authenticated) {
          // Face ID authentication successful
          // Navigation will be handled by useEffect when user state changes
          return;
        }
      }
    } catch (err) {
      setError('Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    // This would typically open a forgot password modal or navigate to reset page
    alert('Password reset functionality would redirect to reset password page.');
  };

  const renderAuthForm = () => {
    switch (authMethod) {
      case 'otp':
        return <OTPForm onSubmit={handleAuthSubmit} loading={loading} />;
      case 'faceid':
        return <FaceIDForm onSubmit={handleAuthSubmit} loading={loading} />;
      default:
        return (
          <StandardLoginForm 
            onSubmit={handleAuthSubmit} 
            loading={loading}
            onToggleMethod={setAuthMethod}
          />
        );
    }
  };

  // Show loading while checking auth state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          <span className="text-foreground">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mx-auto mb-4">
            <img 
              src="/assets/images/logohalcomm-1770397762721.png" 
              alt="Halcom Logo" 
              className="w-24 h-24 object-contain"
            />
          </div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">Welcome Back</h1>
          <p className="text-muted-foreground">
            Sign in to your HR Portal Pro account
          </p>
        </div>

        {/* Main Login Card */}
        <div className="bg-card rounded-xl border border-border card-shadow p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-error" />
                <p className="text-sm text-error font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Authentication Method Selector */}
          <div className="mb-6">
            <AuthMethodSelector 
              selectedMethod={authMethod}
              onMethodChange={setAuthMethod}
            />
          </div>

          {/* Authentication Form */}
          <div className="mb-6">
            {renderAuthForm()}
          </div>

          {/* Login Utilities (only for standard login) */}
          {authMethod === 'standard' && (
            <div className="mb-6">
              <LoginUtilities
                rememberMe={rememberMe}
                onRememberMeChange={setRememberMe}
                onForgotPassword={handleForgotPassword}
              />
            </div>
          )}

          {/* Security Badges */}
          <SecurityBadges />
        </div>

        {/* Additional Info */}
        <div className="text-center mt-6">
          <p className="text-sm text-muted-foreground">
            Need help? Contact{' '}
            <button className="text-primary hover:text-primary/80 font-medium">
              IT Support
            </button>
          </p>
        </div>
      </div>
      {/* Background Pattern */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-accent/5 rounded-full blur-3xl"></div>
      </div>
    </div>
  );
};

export default LoginAuthentication;